/**
 * Google Pay Backend Service
 *
 * MFL-driven merchant identity on every surface (web + Android) and every ENV:
 * merchant_info (merchant_id, merchant_origin, auth_jwt, software_info) is
 * forwarded verbatim from /me/payment-services/{id}/tokenization to the
 * Google Pay SDK. MFL's backend signs the auth_jwt authorising the app to
 * invoke Google Pay under MFL's Business Console merchant — no per-brand
 * Google Pay Console registration or env config is involved.
 *
 * MFL's gateway identity (tokenizationSpecification.parameters.gateway and
 * .gatewayMerchantId) is likewise preserved unchanged — those route the
 * tokenized payment to the correct gateway merchant for decryption.
 *
 * @see docs/plans/2026-04-23-google-pay-integration/02-design.md (AD-5)
 * @see https://developers.google.com/pay/api
 */
import type {BackendGooglePayPaymentRequest} from '../../../../../queries/paymentCard/types';
import {getServiceTokenization} from '../../../../../transport/paymentCardApiV2';
import type {SingleUsePaymentToken} from '../../../../../transport/types/checkout';
import {logger} from '../../../../../utils/logger';

import {buildSingleUsePaymentToken} from './googlePayTokenNormalizer';
import type {GooglePayPayment, GooglePayPaymentRequest} from './types';

/**
 * Convert snake_case keys to camelCase. MFL returns
 * tokenization_specification.parameters in snake_case (e.g. gateway_merchant_id)
 * but Google Pay's SDK expects camelCase (gatewayMerchantId) per
 * https://developers.google.com/pay/api/android/reference/request-objects#PaymentGatewayTokenizationSpecification.
 * Without this conversion the native SDK throws
 * "TokenizationParameter key 'gatewayMerchantId' is missing!".
 */
function snakeToCamelKeys(input: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = value;
  }
  return out;
}

function normalizePaymentRequest(backend: BackendGooglePayPaymentRequest): GooglePayPaymentRequest {
  const mi = backend.merchant_info;
  return {
    apiVersion: backend.api_version ?? 2,
    apiVersionMinor: backend.api_version_minor ?? 0,
    merchantInfo: {
      merchantId: mi?.merchant_id,
      merchantName: mi?.merchant_name,
      merchantOrigin: mi?.merchant_origin,
      authJwt: mi?.auth_jwt,
      softwareInfo: mi?.software_info,
    },
    allowedPaymentMethods: backend.allowed_payment_methods.map(m => ({
      type: m.type,
      parameters: {
        allowedAuthMethods: m.parameters.allowed_auth_methods,
        allowedCardNetworks: m.parameters.allowed_card_networks,
      },
      tokenizationSpecification: {
        type: m.tokenization_specification.type,
        parameters: snakeToCamelKeys(m.tokenization_specification.parameters),
      },
    })),
    transactionInfo: {
      totalPriceStatus: backend.transaction_info.total_price_status,
      totalPrice: backend.transaction_info.total_price,
      currencyCode: backend.transaction_info.currency_code,
      countryCode: backend.transaction_info.country_code,
    },
  };
}

class GooglePayService {
  private readonly LOG_TAG = 'GooglePayService';
  private currentServiceId: string | null = null;

  getServiceId(): string | null {
    return this.currentServiceId;
  }

  clearServiceId(): void {
    this.currentServiceId = null;
  }

  /**
   * Fetch PaymentDataRequest from backend. MFL's merchantInfo is forwarded
   * verbatim on every platform (see file-level comment).
   */
  async getPaymentRequest(
    serviceId: string,
    remainingBalanceCents?: number,
  ): Promise<GooglePayPaymentRequest> {
    logger.info(this.LOG_TAG, 'Fetching payment request from backend', {
      serviceId,
      remainingBalanceCents,
    });

    this.currentServiceId = serviceId;

    try {
      const response = await getServiceTokenization(serviceId);

      if (response.service_type !== 'google_pay') {
        throw new Error(`Expected google_pay service, got ${response.service_type}`);
      }

      // MFL is the sole source of merchant identity — a missing merchant_id
      // means a misprovisioned google_pay service. Fail early with a clear
      // error instead of opening a sheet Google will reject with a generic
      // developer error at the end of checkout.
      if (!response.fields.payment_request.merchant_info?.merchant_id) {
        throw new Error(
          'Backend did not return merchant_info.merchant_id — google_pay service is misprovisioned for this shop',
        );
      }
      if (!response.fields.payment_request.merchant_info.auth_jwt) {
        logger.warn(this.LOG_TAG, 'merchant_info.auth_jwt missing from tokenization response');
      }

      let request = normalizePaymentRequest(response.fields.payment_request);

      if (remainingBalanceCents !== undefined) {
        const remainingBalanceDollars = (remainingBalanceCents / 100).toFixed(2);
        request = {
          ...request,
          transactionInfo: {
            ...request.transactionInfo,
            totalPrice: remainingBalanceDollars,
          },
        };
        logger.info(this.LOG_TAG, 'Overriding total for gift card split payment', {
          original: response.fields.payment_request.transaction_info.total_price,
          remaining: remainingBalanceDollars,
        });
      }

      logger.info(this.LOG_TAG, 'Payment request ready', {
        total: request.transactionInfo.totalPrice,
        currency: request.transactionInfo.currencyCode,
        gateway: request.allowedPaymentMethods[0]?.tokenizationSpecification?.parameters?.gateway,
        merchantId: request.merchantInfo.merchantId,
        merchantOrigin: request.merchantInfo.merchantOrigin,
        hasAuthJwt: Boolean(request.merchantInfo.authJwt),
      });

      return request;
    } catch (error) {
      this.currentServiceId = null;
      logger.error(this.LOG_TAG, 'Failed to fetch payment request', {
        error: error instanceof Error ? error.message : String(error),
        serviceId,
      });
      throw error;
    }
  }

  /**
   * Build single_use_payment_token for checkout.
   *
   * Google Pay's tokenizationData.token is a JSON string - pass through unchanged.
   */
  buildPaymentToken(payment: GooglePayPayment, paymentServiceId?: string): SingleUsePaymentToken {
    const serviceId = paymentServiceId ?? this.currentServiceId;
    if (!serviceId) {
      throw new Error('Service ID required. Call getPaymentRequest() first or pass explicitly.');
    }

    logger.info(this.LOG_TAG, 'Building payment token for checkout', {
      paymentServiceId: serviceId,
      network: payment.paymentMethodData.info?.cardNetwork,
    });

    return buildSingleUsePaymentToken(payment, serviceId);
  }
}

export const googlePayService = new GooglePayService();
export default googlePayService;
